/**
 * DocHub "AB Tower" click metrics.
 *
 * On the legacy PHP form page every CTA carried a `data-metric-ab-tower` attribute and a global
 * `clickTracker()` script POSTed `{ abTower }` to `/api/send-metrics` on click. That script is not
 * shipped on Herald (`?hrld=1`) pages — the DocHub components render no `data-metric-*` attributes
 * and no tracker bundle — so the CTA-click metric (`dh_lp_get_form_button_click`) was silently lost.
 * This helper restores it from inside the component itself, with no external script.
 *
 * The `api` route group requires no CSRF (throttle + SubstituteBindings only), and Herald pages ship
 * no `meta[name=csrf-token]`, so a plain same-origin `fetch` is enough; the token is attached only
 * when present (parity with the legacy helper and `rating:dochub-vote`). `keepalive: true` lets the
 * POST outlive the CTA's own navigation — the button is a plain `<a href>` that leaves the page.
 */

const SEND_METRICS_ROUTE = '/api/send-metrics';

/**
 * Fire-and-forget the AB Tower click metric. Never rejects — a failed metric must not break the
 * click. No-ops when `abTower` is empty (metric disabled for the page) or outside the browser.
 */
export const sendAbTowerMetric = (abTower?: string, endpoint: string = SEND_METRICS_ROUTE): void => {
  if (!abTower || typeof fetch === 'undefined') return;

  const csrf = typeof document !== 'undefined'
    ? document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content
    : undefined;

  try {
    void fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRF-TOKEN': csrf } : {}) },
      body: JSON.stringify({ abTower }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* navigation may abort the request; the metric is best-effort */
  }
};
